import React, { useEffect, useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { connect } from 'react-redux';
import Icon from '@mdi/react';
import { mdiMagnify, mdiChevronDown } from '@mdi/js';
import { makeApiCall, isWithinTradingHours } from '../../../helpers/utils';
import EmptyState from '../../../components/pages/markets/component/empty-state';
import { useInterval } from '../../../helpers/custom-hooks';
import isEmpty from "lodash/isEmpty";
import { BQLink } from "../../atoms/bqlink";
import { isMobileOnly as isMobile } from "react-device-detect";
import './budget-impacts-page.m.css';

const BudgetImpactsPage = ({ features }) => {
  const [currentTab, setCurrentTab] = useState('');
  const [currentSector, setCurrentSector] = useState(null);
  const [cardsData, setCardsData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tabs, setTabs] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cardLoading, setCardLoading] = useState(false); // NEW: Track card loading state
  const [dropdownLabel, setDropdownLabel] = useState('More');

  const [ref, inView] = useInView();
  const dropdownRef = useRef(null);

  let maxPositivePercentage = 0;
  let maxNegativePercentage = 0;
  let negativeIndex = 0;
  let bgColor = '#FFFFFF';
  let greenColor = 0;
  let redColor = 0;

  useEffect(() => {
    try {
      makeApiCall(features?.heatmap?.sector_url).then((res) => {
        if (res && res?.length > 0) {
          setCurrentTab(res?.[0]?.EmbedCode.replace(/\s/g, '_').replace(/&/g, '_'));
          const data = [];
          res.forEach((item) => {
            let slug = '';
            if (!!item?.Link) {
              const url = new URL(item?.Link);
              slug = url.pathname;
            }
            data.push({
              name: item?.EmbedCode,
              key: item?.EmbedCode.replace(/\s/g, '_').replace(/&/g, '_'),
              hero_image: item?.FullImage || '',
              headline: item?.Title || '',
              slug,
              sectorId: item?.author,
            });
          });
          setTabs(data);
        }
      });
    } catch (e) {
      console.error('Error in fetching budget impacts tabs', e);
    }
  }, []);

  useEffect(() => {
    if (initialLoading && tabs.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const sector = urlParams.get('sector');
      const firstTab = !!sector ? tabs?.find(tab => tab?.key?.toLowerCase() === sector?.toLowerCase()) : tabs[0];
      if (!isEmpty(firstTab)) {
        setInitialLoading(false);
        setCurrentSector(firstTab.sectorId);
        setCurrentTab(firstTab.key);
        getSectorData(firstTab.sectorId, true);
        const currentPath = window.location.pathname;
        history.pushState(null, '', currentPath);
        setDropdownLabel(isMobile ? firstTab.name : !!sector ? firstTab.name : 'More');
      }
    }
  }, [tabs]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleScroll = (event) => {
      // If scroll happened INSIDE dropdown → ignore
      if (dropdownRef.current?.contains(event.target)) {
        return;
      }
      // Scroll happened outside → close dropdown
      setIsDropdownOpen(false);
    };
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    // Capture = true is required to detect scroll on all parents
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDropdownOpen]);

  useInterval(() => {
    getSectorData(currentSector);
  }, 60000);

  const getSectorData = (id, initialLoading = false, tabSwitch = false) => {
    if (
      (isWithinTradingHours(features?.trading_holidays, features?.special_trading_days) &&
        inView) ||
      initialLoading ||
      tabSwitch
    ) {
      if (initialLoading || tabSwitch) {
        setCardLoading(true);
      }

      makeApiCall(`/foapi/market/getSectorStocks?sectorId=${id}`)
        .then((res) => {
          if (res?.responseCode === 200) {
            setCardsData(res.data || []); // empty array is VALID
          } else {
            setCardsData([]);
          }
        })
        .catch(() => {
          setCardsData([]);
        })
        .finally(() => {
          //  ALWAYS stop loading
          if (initialLoading || tabSwitch) {
            setCardLoading(false);
          }
        });
    }
  };

  const selectTab = (e, tab, fromDropdown = false) => {
    setCurrentTab(tab.key);

    if (currentSector !== tab.sectorId) {
      getSectorData(tab.sectorId, false, true);
      setCurrentSector(tab.sectorId);
    }

    if (fromDropdown) {
      setDropdownLabel(tab.name);
    } else {
      setDropdownLabel('More');
    }

    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const renderCards = () => {
    if (!cardsData.length) return null;
    const sortedData = cardsData.sort((a, b) => b.changePer - a.changePer);
    return sortedData.map((card, index) => {
      if (maxPositivePercentage === 0) {
        maxPositivePercentage = card.changePer;
        greenColor = (80 / maxPositivePercentage) * card.changePer;
      } else {
        greenColor = 80 + 10 * (index + 1);
      }

      if (maxNegativePercentage === 0 && card.changePer < 0) {
        maxNegativePercentage = card.changePer;
        redColor = 235;
        negativeIndex = 2;
      } else {
        redColor = 235 - (235 / sortedData.length) * negativeIndex;
        negativeIndex++;
      }

      if (card.changePer > 0) {
        bgColor = `rgb(0,${greenColor},0)`;
      } else if (card.changePer < 0) {
        redColor = redColor === 0 ? 100 : redColor;
        bgColor = `rgb(${redColor},0,0)`;
      } else if (card.changePer === 0) {
        bgColor = `#3A3A3A`;
      }

      return (
        <div
          styleName="card"
          key={card.name.replace(/\s/g, '_') + '_' + index}
          role="button"
          style={{
            background: `${bgColor} 0 0 no-repeat padding-box`,
          }}
        >
          <div
            styleName="stock-name"
            title={card.name}
            onClick={() => takeToStockPage(card?.exchangeSymbol, card?.entityId)}
            onTouchStart={() => takeToStockPage(card?.exchangeSymbol, card?.entityId)}
          >
            {card.name}
          </div>
          <div styleName="price-ctx">
            <span styleName="current-price">{card.lastPrice}</span>
            <div styleName="change-ctx">
              {card.changePer !== 0 && (
                <span styleName={`change-symbol ${card.changePer < 0 ? 'negative-change' : ''}`}>
                  {card.changePer < 0 ? '▼' : '▲'}
                </span>
              )}
              <span styleName={`change-perc ${card.changePer === 0 ? 'no-change' : ''}`}>
                {card.changePer}%
              </span>
            </div>
          </div>
        </div>
      );
    });
  };

  const takeToStockPage = (symbol, entityId) => {
    try {
      makeApiCall(`/api/v1/entities?type=stock&q=${symbol}&limit=30`).then((res) => {
        if (
          res.entities &&
          res.entities.length > 0 &&
          res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId)
        ) {
          window.open(
            `/stock/${res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId)['stock-id']}/${res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId).slug.replace('-stock', '')}`,
            '_blank',
          );
        }
      });
    } catch (e) {
      console.error('Error in fetching stock entity', e);
    }
  };

  const PRIMARY_TABS_COUNT = 4;
  const primaryTabs = !isMobile ? tabs.slice(0, PRIMARY_TABS_COUNT) : [];
  const dropdownTabs = isMobile ? tabs : tabs.slice(PRIMARY_TABS_COUNT);

  const isDropdownActive =
    dropdownLabel !== 'More' || dropdownTabs.some((tab) => tab.key === currentTab);

  const filteredDropdownSectors = dropdownTabs?.filter((tab) =>
    tab.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <>
      <div ref={ref} styleName="budget-impacts-ctx" className="section-fluid">
        <div styleName="budget-impacts-inner-ctx" className="container">
          {/* Heading Section */}
          <div styleName="headline-ctx">
            <div styleName="title">Heatmap</div>
            <div styleName="drop-list">
              Sectors {/* <Icon path={mdiChevronDown} size="24px" /> */}
            </div>
          </div>

          {/* FIXED: Show shimmer only on initial load */}
          {initialLoading ? (
            <>
              {/* Tabs Header Shimmer */}
              <div styleName="tabs-header">
                <div styleName="tabs-container">
                  <div styleName="tabs-wrapper tab-loading">
                    <div styleName="sectors" className="mobile-only"></div>
                    {!isMobile &&
                      Array.apply(null, Array(5)).map((_, idx) => (
                        <div key={idx} styleName="sectors" className="desktop-only"></div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Story Shimmer */}
              <div styleName="story loading">
                <div styleName="first-row"></div>
                <div styleName="second-row"></div>
                <div styleName="third-row"></div>
              </div>

              {/* Card Section Shimmer */}
              <div styleName="story-heatmap-ctx">
                <div styleName="cards-ctx">
                  <div styleName="cards">
                    {Array.apply(null, Array(16)).map((_, idx) => (
                      <div key={idx} styleName="card card-loading">
                        <div styleName="first-row"></div>
                        <div styleName="second-row"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Tabs Header */}
              <div styleName="tabs-header">
                <div styleName="tabs-container">
                  <div styleName="tabs-wrapper">
                    {/* FIRST 4 TABS */}
                    {primaryTabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={(e) => selectTab(e, tab, false)}
                        styleName={`tab-btn ${currentTab === tab.key ? 'active' : ''}`}
                      >
                        {tab.name}
                      </button>
                    ))}

                    {/* DROPDOWN */}
                    {dropdownTabs.length > 0 && (
                      <div styleName="dropdown" ref={dropdownRef}>
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          styleName={`tab-btn dropdown-toggle ${isDropdownActive ? 'active' : ''}`}
                        >
                          {dropdownLabel}

                          <div styleName={`chevron ${isDropdownOpen ? 'open' : ''}`}>
                            <Icon path={mdiChevronDown} size="24px" />
                          </div>
                        </button>

                        {isDropdownOpen && (
                          <div styleName="dropdown-menu">
                            <div styleName="dropdown-search">
                              <div styleName="search-icon">
                                <Icon path={mdiMagnify} size="20px" color="#A5AFB4" />
                              </div>
                              <input
                                type="text"
                                placeholder="Search Sectors"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </div>

                            <div styleName="dropdown-list">
                              {filteredDropdownSectors.map((tab) => (
                                <button
                                  key={tab.key}
                                  onClick={(e) => selectTab(e, tab, true)}
                                  styleName={`dropdown-item ${currentTab === tab.key ? 'active' : ''}`}
                                >
                                  {tab.name}
                                </button>
                              ))}

                              {filteredDropdownSectors.length === 0 && (
                                <div styleName="no-results">No sectors found</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Story Section */}
              {!!tabs.find((tab) => tab.key === currentTab).hero_image && (
                <BQLink href={tabs.find((tab) => tab.key === currentTab).slug} target="_blank">
                  <div
                    styleName="story"
                    style={{
                      background: `${isMobile ? 'linear-gradient(179deg, #00000000 0%, #262626 100%)' : 'linear-gradient(269deg, #00000000 0%, #262626 50%)'}, url(${tabs.find((tab) => tab.key === currentTab).hero_image}) no-repeat`,
                    }}
                  >
                    <div styleName="story-headline-ctx">
                      <div styleName="current-tab-name">
                        {tabs.find((tab) => tab.key === currentTab).name}
                      </div>
                      <div styleName="headline">
                        {tabs.find((tab) => tab.key === currentTab).headline}
                      </div>
                    </div>
                  </div>
                </BQLink>
              )}

              {/* Card Section */}
              <div styleName="story-heatmap-ctx">
                <div styleName="cards-ctx">
                  {/* FIXED: Show card shimmer when loading cards */}
                  {cardLoading ? (
                    <div styleName="cards">
                      {Array.apply(null, Array(12)).map((_, idx) => (
                        <div key={idx} styleName="card card-loading">
                          <div styleName="first-row"></div>
                          <div styleName="second-row"></div>
                        </div>
                      ))}
                    </div>
                  ) : cardsData.length === 0 ? (
                    <EmptyState width="280px" height="280px" />
                  ) : (
                    <div styleName="cards">{renderCards()}</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  cdnHost: 'media.assettype.com',
  features: state.qt.config.features,
});

export default connect(mapStateToProps)(BudgetImpactsPage);
